import {
  Button,
  MessageBar,
  MessageBarBody,
  Text,
} from '@fluentui/react-components';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import {
  DataHubClientProvider,
  useDataHubClient,
} from '../../context/DataHubClientContext.js';
import type { AccessControlGroup } from '../../services/accessControlService.interface.js';
import { AccessControlService } from '../../services/accessControlService.js';
import { DataHubService } from '../../services/dataHubService.js';
import { WorkspaceService } from '../../services/workspaceService.js';

import { createPrivateWorkspace } from './privateWorkspace.js';
import type { PrivateWorkspaceCreateSectionProps } from './PrivateWorkspaceCreateSection.interface.js';
import { WorkspaceRoleEditor } from './WorkspaceRoleEditor.js';

const CREATE_PRIVATE_WORKSPACE_ERROR =
  'Private workspace creation failed. No workspace was created.';

function PrivateWorkspaceCreateFields({
  name,
  displayName,
  description,
  canCreate,
  onCreated,
}: PrivateWorkspaceCreateSectionProps) {
  const workspaceClient = useHttpClient();
  const dataHubClient = useDataHubClient();
  const [ownerGroups, setOwnerGroups] = useState<AccessControlGroup[]>([]);
  const [contributorGroups, setContributorGroups] = useState<
    AccessControlGroup[]
  >([]);
  const [readerGroups, setReaderGroups] = useState<AccessControlGroup[]>([]);

  const dependencies = useMemo(
    () => ({
      workspaceService: new WorkspaceService(workspaceClient),
      dataHubService: new DataHubService(dataHubClient),
      accessControlService: new AccessControlService(dataHubClient),
    }),
    [dataHubClient, workspaceClient],
  );

  const createMutation = useMutation({
    mutationFn: () =>
      createPrivateWorkspace(
        {
          name,
          displayName,
          description,
          roles: {
            owner: ownerGroups.map((group) => group.id),
            contributor: contributorGroups.map((group) => group.id),
            reader: readerGroups.map((group) => group.id),
          },
        },
        dependencies,
      ),
    onSuccess: ({ workspace }) => onCreated(workspace),
  });

  const hasNonOwnerAssignments =
    contributorGroups.length > 0 || readerGroups.length > 0;
  const needsOwnerGroup = ownerGroups.length === 0 && hasNonOwnerAssignments;
  const isCreateDisabled =
    !canCreate || needsOwnerGroup || createMutation.isPending;

  return (
    <>
      <Text>
        Access is enforced through a restricted Data Lake dataset associated
        with this workspace. Groups are optional during creation. Until access
        is configured, only the creator can use the workspace.
      </Text>
      <WorkspaceRoleEditor
        label="Owners"
        description="Can manage workspace settings and access."
        groups={ownerGroups}
        onChange={setOwnerGroups}
        disabled={createMutation.isPending}
      />
      {needsOwnerGroup && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Select an Owner group before assigning Contributors or Readers.
          </MessageBarBody>
        </MessageBar>
      )}
      <WorkspaceRoleEditor
        label="Contributors"
        description="Can create and update workspace resources."
        groups={contributorGroups}
        onChange={setContributorGroups}
        disabled={createMutation.isPending}
      />
      <WorkspaceRoleEditor
        label="Readers"
        description="Can view workspace resources."
        groups={readerGroups}
        onChange={setReaderGroups}
        disabled={createMutation.isPending}
      />
      {createMutation.error !== null && (
        <MessageBar intent="error">
          <MessageBarBody>
            {createMutation.error instanceof Error
              ? createMutation.error.message
              : CREATE_PRIVATE_WORKSPACE_ERROR}
          </MessageBarBody>
        </MessageBar>
      )}
      <Button
        appearance="primary"
        disabled={isCreateDisabled}
        onClick={() => createMutation.mutate()}
      >
        {createMutation.isPending
          ? 'Creating private workspace...'
          : 'Create private workspace'}
      </Button>
    </>
  );
}

export function PrivateWorkspaceCreateSection(
  props: PrivateWorkspaceCreateSectionProps,
) {
  return (
    <DataHubClientProvider>
      <PrivateWorkspaceCreateFields {...props} />
    </DataHubClientProvider>
  );
}
